/** Headings never need deep React trees; stop before Mobile Safari stack limits. */
const MAX_PLAIN_TEXT_DEPTH = 32;

/**
 * Build a stable heading `id` from PortableText children without calling
 * `.toString()` on React elements (which can recurse / yield useless ids).
 *
 * @param children - PortableText block children (string, element, or array)
 * @returns Underscore-joined plain text suitable for an HTML id
 */
export const portableTextHeadingId = (children: unknown): string => {
	const text = collectPlainText(children, new WeakSet(), 0)
		.trim()
		.replace(/\s+/g, "_");
	return text;
};

/**
 * Walk PortableText/React-like children for plain text.
 * Uses a WeakSet + depth cap so cyclic or pathological trees cannot stack-overflow.
 *
 * @param node - Current child node
 * @param seen - Objects already visited on this walk
 * @param depth - Current recursion depth
 * @returns Concatenated plain text
 */
const collectPlainText = (
	node: unknown,
	seen: WeakSet<object>,
	depth: number,
): string => {
	if (node == null || typeof node === "boolean") {
		return "";
	}

	if (typeof node === "string" || typeof node === "number") {
		return String(node);
	}

	if (depth >= MAX_PLAIN_TEXT_DEPTH) {
		return "";
	}

	if (Array.isArray(node)) {
		return node
			.map((child) => collectPlainText(child, seen, depth + 1))
			.join("");
	}

	if (typeof node === "object" && node !== null && "props" in node) {
		if (seen.has(node)) {
			return "";
		}
		seen.add(node);

		const props = (node as { props?: { text?: unknown; children?: unknown } })
			.props;
		if (typeof props?.text === "string" || typeof props?.text === "number") {
			return String(props.text);
		}
		return collectPlainText(props?.children, seen, depth + 1);
	}

	return "";
};

export { MAX_PLAIN_TEXT_DEPTH };
